import React, { useEffect, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from './authConfig';
import apiBaseUrl from './apiBaseUrl';
import './styles.css';

function KeywordsetsPage() {
  const { instance, accounts } = useMsal();
  const [keywordSets, setKeywordSets] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [editName, setEditName] = useState('');
  const [editKeywords, setEditKeywords] = useState('');
  const [editActive, setEditActive] = useState(false);
  const [editNDaysBefore, setEditNDaysBefore] = useState('');
  
  const [isNew, setIsNew] = useState(false);
  const API_URL = `${apiBaseUrl}/api/keywordsets`;

  const authFetch = async (url, options = {}) => {
    const account = accounts[0] || instance.getActiveAccount() || instance.getAllAccounts()[0];
    if (!account) {
      throw new Error('No authenticated user available.');
    }

    const tokenResponse = await instance.acquireTokenSilent({
      ...loginRequest,
      account,
    });

    return fetch(url, {
      ...options,
      headers: {
        ...(options.headers || {}),
        Authorization: `Bearer ${tokenResponse.idToken}`,
      },
    });
  };

  // Helper function to get default date (30 days before today)
  const getDefaultDate = () => {
    const today = new Date();
    const defaultDate = new Date(today);
    defaultDate.setDate(today.getDate() - 30);
    return defaultDate.toISOString().split('T')[0]; // Format as yyyy-mm-dd
  };
  const [editDateFrom, setEditDateFrom] = useState(getDefaultDate());

  useEffect(() => {
    authFetch(API_URL)
      .then(res => {
        if (!res.ok) throw new Error(`Server returned ${res.status}`);
        return res.json();
      })
      .then(data => {
        if (!Array.isArray(data)) {
          console.error('Unexpected response from keyword sets API:', data);
          return;
        }
        setKeywordSets(data);
        if (data.length > 0) {
          setSelectedId(data[0].id);
        }
      })
      .catch(error => {
        console.error('Error fetching keyword sets:', error);
      });
  }, []);

  useEffect(() => {
    if (isNew) return;
    const selectedSet = keywordSets.find(set => set.id === selectedId);
    if (selectedSet) {
      setEditName(selectedSet.name);
      setEditKeywords(selectedSet.keywords);
      setEditActive(selectedSet.active === true || selectedSet.active === 'true');
      setEditDateFrom(selectedSet.dateFrom || getDefaultDate());
      setEditNDaysBefore(selectedSet.nDaysBefore || '');
    }
  }, [selectedId, keywordSets, isNew]);

  const handleSelectChange = (e) => {
    setSelectedId(e.target.value);
    setIsNew(false);
  };

const handleSave = () => {
  const keywordsStr = editKeywords.trim();

  // commented out for the moment
  // check if at least 3 valid characters are in keywrods
  //   if (!editName || keywordsStr.length === 0 || !/^(.{3,})$/.test(keywordsStr)) {
  //   alert('Name and keywords must be filled with at least 3 valid characters.');
  //   return;
  // }

  // new validation, if there is no input for keyword, use empty string as the value
  if (!editName) {
    alert('Name must be filled.');
    return;
  }
  
  // Only validate nDaysBefore if a value is provided
  if (editNDaysBefore && editNDaysBefore !== '' && (isNaN(editNDaysBefore) || Number(editNDaysBefore) < 1 || !Number.isInteger(Number(editNDaysBefore)))) {
    alert('Days Before must be a positive integer greater than 0.');
    return;
  }
  
  const nameExists = keywordSets.some(ks => ks.name === editName && ks.id !== selectedId);

  if (isNew) {
    if (nameExists) {
      alert('A keyword set with this name already exists. Please choose a different name.');
      return;
    }

    authFetch(API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ 
        name: editName, 
        keywords: keywordsStr, 
        active: editActive, 
        dateFrom: '', 
        nDaysBefore: editNDaysBefore && editNDaysBefore !== '' ? Number(editNDaysBefore) : null 
      })
    })
      .then(res => {
        if (!res.ok) throw new Error('Failed to create new keyword set');
        return res.json();
      })
      .then(newSet => {
        // Only set other sets to inactive if the new set is active
        const updatedExistingSets = editActive 
          ? keywordSets.map(ks => ({ ...ks, active: false }))
          : keywordSets;
        setKeywordSets([...updatedExistingSets, newSet]);
        setSelectedId(newSet.id);
        setEditName(newSet.name);
        setEditKeywords(newSet.keywords);
        setEditActive(newSet.active === true || newSet.active === 'true');
        setEditDateFrom(newSet.dateFrom);
        setEditNDaysBefore(newSet.nDaysBefore || '');
        setIsNew(false);
      })
      .catch(error => {
        console.error('Error creating keyword set:', error);
        alert('Failed to create new keyword set. Check console for details.');
      });
  } else {
    const selectedSet = keywordSets.find(set => set.id === selectedId);
    if (!selectedSet || (editName !== selectedSet.name && nameExists)) {
      alert('A keyword set with this name already exists. Please choose a different name.');
      return;
    }

    const currentActiveSet = keywordSets.find(set => set.active);
    if (currentActiveSet && currentActiveSet.id === selectedId && !editActive) {
      alert('Cannot deactivate the last active keyword set. There must always be one active set.');
      return;
    }

    authFetch(`${API_URL}/${selectedId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ 
        name: editName, 
        keywords: keywordsStr, 
        active: editActive, 
        dateFrom: '', 
        nDaysBefore: editNDaysBefore && editNDaysBefore !== '' ? Number(editNDaysBefore) : null 
      })
    })
      .then(res => {
        if (!res.ok) throw new Error('Failed to update keyword set');
        return res.json();
      })
      .then(updatedSet => {
        // Update the keywordSets to reflect the new values
        // Only set other sets to inactive if this set is being made active
        const updatedKeywordSets = keywordSets.map(set =>
          set.id === selectedId 
            ? updatedSet 
            : (editActive ? { ...set, active: false } : set)
        );
        setKeywordSets(updatedKeywordSets);
        setEditName(updatedSet.name);
        setEditKeywords(updatedSet.keywords);
        setEditActive(updatedSet.active === true || updatedSet.active === 'true');
        setEditDateFrom(updatedSet.dateFrom);
        setEditNDaysBefore(updatedSet.nDaysBefore || '');
      })
      .catch(error => {
        console.error('Error updating keyword set:', error);
        alert('Failed to update keyword set. Check console for details.');
      });
  }
};




const handleDelete = () => {
  const selectedSet = keywordSets.find(set => set.id === selectedId);
  if (selectedSet && selectedSet.active) {
    alert('Cannot delete an active keyword set. Please make another set active first.');
    return;
  }

  if (!window.confirm('Delete this keyword set?')) return;

  authFetch(`${API_URL}/${selectedId}`, { method: 'DELETE' })
    .then(res => {
      if (!res.ok) throw new Error('Failed to delete keyword set');
      const updatedSets = keywordSets.filter(set => set.id !== selectedId);
      setKeywordSets(updatedSets);
      if (updatedSets.length > 0) setSelectedId(updatedSets[0].id);
      else setSelectedId('');
    })
    .catch(error => {
      console.error('Error deleting keyword set:', error);
      alert('Failed to delete keyword set. Check console for details.');
    });
};


const handleNew = () => {
  setSelectedId(null);
  setEditName('');
  setEditKeywords('');
  setEditActive(false);
  setEditDateFrom('');
  setEditNDaysBefore('');
  setIsNew(true);
};  const handleCancel = () => {
    setIsNew(false);
    if (keywordSets.length > 0)
      setSelectedId(keywordSets[0].id);
  };

  return (
    <div className="keywordsets-container">
      <h1>Keyword Sets Editor</h1>
      {keywordSets.length > 0 && !isNew && (
        <div className="form-field">
          <label htmlFor="set-select" className="form-label">Select Keyword Set:&nbsp;</label>
          <select id="set-select" value={selectedId} onChange={handleSelectChange} className="form-select">
            {keywordSets.map(ks => (
              <option key={ks.id} value={ks.id}>{ks.name}</option>
            ))}
          </select>
        </div>
      )}

      <div className="form-section">
        <div className="form-field">
          <label className="form-label">
            Name:<br />
            <input
              type="text"
              value={editName}
              onChange={e => setEditName(e.target.value)}
              className="form-input"
            />
          </label>
        </div>
        <div className="form-field">
          <label className="form-label">
            Keywords (space-separated):<br />
            <input
              type="text"
              value={editKeywords}
              onChange={e => setEditKeywords(e.target.value)}
              className="form-input"
            />
          </label>
        </div>
        <div className="form-field">
          <label className="form-label">
            Date From:<br />
            <input
              type="date"
              value={editDateFrom || getDefaultDate()}
              onChange={e => setEditDateFrom(e.target.value)}
              className="form-input"
              style={{ display: 'none' }}
            />
          </label>
        </div>
        <div className="form-field">
          <label className="form-label">
            Days Before:<br />
            <input
              type="number"
              value={editNDaysBefore}
              onChange={e => setEditNDaysBefore(e.target.value)}
              min="1"
              className="form-input"
            />
          </label>
        </div>
        <div className="form-field">
          <label className="form-label">
            Active:&nbsp;
            <input
              type="checkbox"
              checked={editActive}
              onChange={e => setEditActive(e.target.checked)}
              className="form-checkbox"
            />
          </label>
        </div>
        <div className="button-container">
          <button onClick={handleSave} className="api-button">
            {isNew ? 'Save' : 'Update'}
          </button>
          {isNew ? (
            <button onClick={handleCancel} className="api-button">Cancel</button>
          ) : (
            <>
              <button onClick={handleDelete} className="api-button">Delete</button>
              <button onClick={handleNew} className="api-button">New</button>
            </>
          )}
        </div>
      </div>
      
      <div className="note-section">
        <h3>Note:</h3>
        <p>User manage multiple "keyword sets" on this page.</p>
        <p>Only one "keyword set" can be active at a time.</p>
      </div>
    </div>
  );
}

export default KeywordsetsPage;
